import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Plus, Search } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  emptyState,
  iconSize,
  bodySecondary,
  motionFadeUp,
  motionPageTransition,
} from "../../../../../lib/design-system";
import DiscussionCard from "./DiscussionCard";
import DiscussionForm from "./DiscussionForm";
import { DiscussionListSkeleton } from "../../../../StudentDashboard/Components/ui/Skeleton";

const DiscussionList = ({
  discussions,
  onView,
  onDelete,
  onCreate,
  loading,
  error,
  activeTab,
  showForm,
  onToggleForm,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return discussions;
    const q = searchQuery.toLowerCase();
    return discussions.filter(
      (d) =>
        d.title?.toLowerCase().includes(q) ||
        d.content?.toLowerCase().includes(q) ||
        d.author?.name?.toLowerCase().includes(q)
    );
  }, [discussions, searchQuery]);

  return (
    <motion.div {...motionPageTransition} key="discussion-list">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
        <h4 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">
          {activeTab === "teacher"
            ? "Teacher Discussions"
            : "Course Discussions"}
        </h4>
        <button
          className={cn(
            "flex items-center gap-1.5 px-4 py-2 text-sm rounded-sm",
            "bg-accent1 text-white hover:bg-accent1/90 transition-colors"
          )}
          onClick={onToggleForm}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Discussion</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Search bar */}
      {discussions.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search discussions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full pl-9 pr-4 py-2 text-sm rounded-sm",
              "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
              "text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500",
              "focus:outline-none focus:ring-1 focus:ring-accent1 focus:border-accent1",
              "transition-colors"
            )}
          />
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-sm border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <AnimatePresence mode="wait">
        {showForm && (
          <DiscussionForm
            key="form"
            onSubmit={onCreate}
            onCancel={onToggleForm}
            loading={loading}
          />
        )}
      </AnimatePresence>

      {loading && discussions.length === 0 ? (
        <DiscussionListSkeleton />
      ) : filtered.length === 0 ? (
        <motion.div {...motionFadeUp} className={emptyState}>
          <MessageSquare
            className={cn(
              iconSize.empty,
              "mx-auto mb-3 text-gray-400 dark:text-gray-500"
            )}
          />
          <p className={bodySecondary}>
            {searchQuery.trim()
              ? "No discussions match your search."
              : "No discussions found. Create a new discussion to get started."}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {filtered.map((discussion, index) => (
            <DiscussionCard
              key={discussion._id}
              discussion={discussion}
              index={index}
              onView={onView}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default DiscussionList;
